/**
 * @file ProductFilterDropdown.tsx
 *
 * Generic multi-select filter dropdown used for both the stock and category
 * filters in the products toolbar. Selections are staged ("pending") in the
 * panel and only committed on Apply, matching the reviewed reference behaviour.
 * Closes on outside click or the Cancel/Close controls.
 */

import { useRef, useState } from 'react';
import { Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { useClickOutside } from '../hooks/use-click-outside';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface ProductFilterDropdownProps {
  /** Button + legend label (e.g. "Stock", "Category"). */
  label: string;
  /** Label for the reset action in the panel header. */
  resetLabel: string;
  options: FilterOption[];
  /** Currently applied values. */
  selected: string[];
  /** Called with the new selection when Apply is clicked. */
  onApply: (values: string[]) => void;
  /** `data-testid` namespace for this filter; defaults to `product-filter`. */
  testId?: string;
}

/**
 * Renders a staged multi-select filter dropdown.
 *
 * @param props - Label, options, current selection, and the apply handler.
 *
 * @returns The filter dropdown element.
 */
export function ProductFilterDropdown({
  label,
  resetLabel,
  options,
  selected,
  onApply,
  testId = 'product-filter',
}: ProductFilterDropdownProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const barRef = useRef<HTMLDivElement>(null);

  useClickOutside(barRef, () => setOpen(false), open);

  const openPanel = (): void => {
    setPending(selected);
    setOpen(true);
  };

  const toggle = (value: string): void => {
    setPending((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const apply = (): void => {
    onApply(pending);
    setOpen(false);
  };

  return (
    <div className="gv-filter-bar" ref={barRef} data-testid={testId}>
      <div className="gv-bar">
        <button
          type="button"
          className="gv-filter-button"
          data-testid={`${testId}-button`}
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openPanel())}
        >
          <span className="gv-button-label">{label}</span>
          <span
            className={`gv-total-selected${selected.length === 0 ? ' gv-hidden' : ''}`}
          >
            {selected.length > 0 ? selected.length : ''}
          </span>
          <Icon
            name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            size="small"
          />
        </button>
      </div>
      {open && (
        <div className="gv-filter-panel" role="region" aria-label={label}>
          <div className="gv-filter-options">
            <div className="gv-panel-header">
              <button
                type="button"
                className="gv-action"
                onClick={() => setPending([])}
              >
                <span>{resetLabel}</span>
              </button>
              <button
                type="button"
                className="gv-btn-close"
                aria-label={intl.formatMessage(adminMessages.productsClose)}
                onClick={() => setOpen(false)}
              >
                <Icon name="close" size="small" />
              </button>
            </div>
            <div className="gv-options-content">
              <fieldset className="gv-options">
                <legend className="gv-filter-name">{label}</legend>
                {options.map((opt) => (
                  <label className="gv-option" key={opt.value}>
                    <input
                      type="checkbox"
                      className="gv-checkbox"
                      data-testid={`${testId}-option`}
                      data-value={opt.value}
                      value={opt.value}
                      checked={pending.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                    />
                    <span className="gv-item-text" title={opt.label}>
                      <span className="gv-item-label">{opt.label}</span>
                      {opt.count !== undefined && (
                        <span className="gv-item-count">({opt.count})</span>
                      )}
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
            <div className="gv-button-group">
              <button
                type="button"
                className="gv-button gv-button-cancel gv-clear"
                onClick={() => setPending([])}
              >
                <FormattedMessage {...adminMessages.productsFilterClear} />
              </button>
              <button
                type="button"
                className="gv-button gv-button-primary"
                data-testid={`${testId}-apply`}
                onClick={apply}
              >
                <FormattedMessage {...adminMessages.productsFilterApply} />
              </button>
              <button
                type="button"
                className="gv-button gv-button-cancel gv-cancel"
                onClick={() => setOpen(false)}
              >
                <FormattedMessage {...adminMessages.productsFilterCancel} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
